import { useState, useCallback } from 'react';
import { useMapEvents } from 'react-leaflet';
import { useSelector } from 'react-redux';
import { LatLng } from 'leaflet';
import { MapViewMode } from '../MapViewTypes';
import { RootState } from '../../../store';

interface UseMapUnitModalReturn {
    /** Whether the modal is visible */
    show: boolean;
    /** Position where the map was clicked */
    position: LatLng | null;
    /** Callback to close the modal */
    onHide: () => void;
}

/**
 * Hook for managing map unit modal state and map click events
 * Must be used inside a MapContainer component
 *
 * @param editMode - Current map view mode
 * @returns Modal state and handlers
 */
export function useMapUnitModal(editMode: MapViewMode): UseMapUnitModalReturn {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState<LatLng | null>(null);
    const activeEditTool = useSelector((state: RootState) => state.mapUi.activeEditTool);

    const map = useMapEvents({
        click: (event) => {
            // Only Edit mode's marker tool consumes clicks to open the add-marker
            // modal; Lines mode routes clicks to the tactical-graphic drawer.
            if (editMode === MapViewMode.EDIT && activeEditTool === 'marker') {
                map.setView(event.latlng);
                setPosition(event.latlng);
                setShow(true);
            }
        },
    });

    const handleHide = useCallback(() => {
        setShow(false);
        setPosition(null);
    }, []);

    return {
        show,
        position,
        onHide: handleHide,
    };
}
